import {
  Avatar, Paper, Box, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { CheckCircle as CheckIcon, Cancel as CancelIcon } from '@mui/icons-material';
import JSZip from 'jszip';

// Shared visual building blocks for the Supervisor-facing report/approval
// pages (SupervisorReports.js, WorkerVisitDetail.js, Approvals.js) —
// extracted so all of them use the exact same look instead of duplicating it.

export const FORM_TYPE_TABS = [
  { key: 'all', label: 'ALL' },
  { key: 'hbnc', label: 'HBNC' },
  { key: 'hbyc', label: 'HBYC' },
  { key: 'nrc', label: 'NRC' },
  { key: 'sncu', label: 'SNCU' },
];

// Visit-day labels, scoped per form type — "visit_3" means day 3 for HBNC
// but month 3 for HBYC/SNCU, so this must never be one flat dict.
export const VISIT_LABELS = {
  hbnc: { visit_1: '१', visit_3: '३', visit_7: '७', visit_14: '१४', visit_21: '२१', visit_28: '२८', visit_42: '४२' },
  hbyc: { visit_3: '३', visit_6: '६', visit_9: '९', visit_12: '१२', visit_15: '१५' },
  nrc: { visit_15days: '१५ दिवस', visit_1month: '१ महिना', visit_2months: '२ महिने', visit_3months: '३ महिने', visit_6months: '६ महिने' },
  sncu: { visit_3: '३', visit_6: '६', visit_9: '९', visit_12: '१२' },
};

// The backend sends the visit's own date as DD/MM/YYYY — displayed here as
// "dd / mm / yy" (spaced slashes, 2-digit year) to stay compact in narrow
// per-visit-day table cells.
export const formatVisitDate = (dmy) => {
  const [d, m, y] = dmy.split('/');
  return `${d} / ${m} / ${(y || '').slice(-2)}`;
};

export function VisitBadge({ value }) {
  // A tiny visual cue so "0 भेटी" doesn't read the same as "17 भेटी" — no
  // hardcoded thresholds beyond "zero is muted, everything else is live".
  const color = value === 0 ? 'default' : 'success';
  return <Chip label={value} size="small" color={color} variant={value === 0 ? 'outlined' : 'filled'} sx={{ fontWeight: 700, minWidth: 42 }} />;
}

export function MotherVisitTable({ mothers, visitDays, formType, maxHeight, isMonthFiltered }) {
  const labels = VISIT_LABELS[formType] || {};
  // "एकूण भेटी" (total visits) only means "all time" with no month filter —
  // with one applied, the count is scoped to that month, so the column
  // header must say so instead of implying a running lifetime total.
  const totalLabel = isMonthFiltered ? 'या महिन्यातील भेटी' : 'एकूण भेटी';
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.5, borderColor: 'grey.200', ...(maxHeight ? { maxHeight } : {}) }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 700, color: 'grey.700', fontSize: '0.76rem' } }}>
            <TableCell>माता</TableCell>
            <TableCell>ABHA क्रमांक</TableCell>
            {visitDays.map((day) => (
              <TableCell key={day} align="center">भेट {labels[day] || day}</TableCell>
            ))}
            <TableCell align="right">{totalLabel}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {mothers.map((m) => (
            <TableRow key={m.formId} hover>
              <TableCell sx={{ fontWeight: 600 }}>{m.motherName}</TableCell>
              <TableCell sx={{ color: 'grey.600', fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem' }}>{m.motherAbha || '-'}</TableCell>
              {visitDays.map((day) => (
                <TableCell key={day} align="center">
                  {m.visits[day] ? (
                    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                      <CheckIcon color="success" fontSize="small" />
                      <Typography sx={{ fontSize: '0.62rem', color: 'grey.500', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                        {formatVisitDate(m.visits[day])}
                      </Typography>
                    </Box>
                  ) : (
                    <CancelIcon sx={{ color: 'grey.300' }} fontSize="small" />
                  )}
                </TableCell>
              ))}
              <TableCell align="right"><VisitBadge value={m.totalVisits} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#fff',
    transition: 'box-shadow 0.15s ease',
    '&:hover': { boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.06)' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.12)' },
  },
};

export function downloadBlob(data, filename) {
  const blob = new Blob([data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// The server writes "एकूण भेटी" into exports even when a month is filtered;
// relabel the file here so the download matches the on-screen column. Plain
// text swap for CSV; for XLSX, every worksheet part of the zip (openpyxl keeps
// header cells as inline strings there) plus shared strings if a file has them.
export async function relabelMonthExport(blob, format) {
  const from = 'एकूण भेटी';
  const to = 'या महिन्यातील भेटी';
  if (format === 'csv') {
    const text = await blob.text();
    return new Blob([text.split(from).join(to)], { type: 'text/csv;charset=utf-8' });
  }
  const zip = await JSZip.loadAsync(blob);
  const parts = Object.keys(zip.files).filter(
    (name) => /^xl\/worksheets\/[^/]+\.xml$/.test(name) || name === 'xl/sharedStrings.xml'
  );
  for (const part of parts) {
    const xml = await zip.file(part).async('string');
    zip.file(part, xml.split(from).join(to));
  }
  return zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export function nameInitial(name) {
  return (name || '?').trim().charAt(0);
}

export function SummaryCard({ title, value, icon: Icon, color }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 3,
        borderColor: 'grey.200',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flex: 1,
        minWidth: 0,
        transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(0,0,0,0.07)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Avatar sx={{ bgcolor: `${color}.lighter`, color: `${color}.main`, width: 50, height: 50 }}>
        <Icon />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.7rem', fontWeight: 800, color: 'grey.900', lineHeight: 1.15 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
      </Box>
    </Paper>
  );
}
